import { useEffect, useState } from "react";
import { Button } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import GenericFormPopup from "../../Fleet/GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import {
  orientationExpenseFieldConfiguration,
  orientationExpenseInitialValues,
} from "../../../formConfigs/Recruiting/orientationExpenseFieldConfig";
import { OrientationExpenseDto } from "../../../interfaces/Recruiting/orientation";

interface UpdateOrientationExpensePopupProps {
  expense: OrientationExpenseDto;
}

const UpdateOrientationExpensePopup = ({
  expense,
}: UpdateOrientationExpensePopupProps) => {
  type OrientationExpenseFormValues = typeof orientationExpenseInitialValues;

  const [open, setOpen] = useState(false);
  const [initialValues, setInitialValues] =
    useState<OrientationExpenseFormValues>(orientationExpenseInitialValues);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  useEffect(() => {
    if (expense) {
      setInitialValues({
        ...orientationExpenseInitialValues,
        driverFirstName: expense.driverFirstName ?? "",
        driverLastName: expense.driverLastName ?? "",
        expenseType:
          expense.expenseType ?? orientationExpenseInitialValues.expenseType,
        cost: expense.cost != null ? String(expense.cost) : "",
        paidBy: expense.paidBy ?? orientationExpenseInitialValues.paidBy,
        notes: expense.notes ?? "",
        hotelDaysNeeded: expense.hotelDaysNeeded ?? "",
      });
    }
  }, [expense]);

  const updateMutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/recruiting/expense`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      queryClient.invalidateQueries({
        queryKey: ["orientationExpenses", expense.orientationId],
      });
      setOpen(false);
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to update expense");
    },
  });

  const submitHandler = (data: any) => {
    updateMutation.mutate({
      ...data,
      cost: Number(data.cost),
      id: expense.id,
      orientationId: expense.orientationId,
    });
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Update
      </Button>
      <GenericFormPopup
        formFields={orientationExpenseFieldConfiguration}
        initialValues={initialValues}
        title="Update Expense"
        onSubmit={submitHandler}
        open={open}
        onClose={() => setOpen(false)}
        loading={updateMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage="Expense updated successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default UpdateOrientationExpensePopup;
